import {
  Button,
  Divider,
  Flex,
  Form,
  Modal,
  Table,
  Text,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useFavoriteLocalStorage } from '../_hooks/local-storage';
import { FavoriteItemSchema } from '../_models/dev-tools';

type SchemaType = Omit<z.infer<typeof FavoriteItemSchema>, 'id'>;

const formSchema = FavoriteItemSchema.omit({ id: true });

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const FavoriteBusinessModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { favorites, addFavorite, updateFavorite, removeFavorite } =
    useFavoriteLocalStorage();

  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<SchemaType>({
    defaultValues: {
      url: '',
      title: '',
      notes: '',
    },
    resolver: zodResolver(formSchema),
  });

  const handleEdit = (id: string) => {
    const favorite = favorites.find((item) => item.id === id);
    if (favorite) {
      setValue('url', favorite.url);
      setValue('title', favorite.title);
      setValue('notes', favorite.notes || '');
      setEditingId(id);
    }
  };

  const handleDelete = (id: string) => {
    removeFavorite(id);
  };

  const submitHandler = async (values: SchemaType) => {
    if (editingId) {
      updateFavorite(editingId, values);
      setEditingId(null);
    } else {
      addFavorite(values);
    }
    reset();
  };

  const handleCancel = () => {
    reset();
    setEditingId(null);
  };

  const columns = [
    {
      title: t('dev-tools.favorite.title'),
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: z.infer<typeof FavoriteItemSchema>) => (
        <a href={record.url} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    },
    {
      title: t('dev-tools.favorite.notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => <Text text={text || ''} />,
    },
    {
      key: 'actions',
      width: 120,
      render: (_: unknown, record: z.infer<typeof FavoriteItemSchema>) => (
        <Flex gap={8}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Flex>
      ),
    },
  ];

  return (
    <Modal
      title={t('dev-tools.favorite-label')}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Flex vertical gap={16}>
        <Text
          variant="footnote"
          weight="regular"
          color="secondary"
          text={t('dev-tools.favorite-description')}
        />

        <Divider />

        <Form
          leavePageAlertCondition={null}
          layout="vertical"
          onFinish={handleSubmit(submitHandler)}
        >
          <Flex gap={16} align="flex-start">
            <TextInputField<SchemaType>
              name="url"
              label={t('dev-tools.favorite.url')}
              control={control}
              errors={errors}
              required
              wrapperStyle={{ width: '100%' }}
            />
            <TextInputField<SchemaType>
              name="title"
              label={t('dev-tools.favorite.title')}
              control={control}
              errors={errors}
              required
              wrapperStyle={{ width: '100%' }}
            />
          </Flex>
          <TextAreaInputField<SchemaType>
            name="notes"
            label={t('dev-tools.favorite.notes')}
            control={control}
            errors={errors}
            style={{ width: '100%' }}
          />
          <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
            {editingId && (
              <Button onClick={handleCancel}>
                <Text text={t('cancel')} />
              </Button>
            )}
            <Button type="primary" htmlType="submit">
              {editingId ? t('update') : t('dev-tools.favorite.add')}
            </Button>
          </Flex>
        </Form>

        <Divider />

        <Table
          dataSource={favorites}
          columns={columns}
          rowKey="id"
          pagination={false}
          locale={{
            emptyText: <Text text={t('dev-tools.favorite.empty')} />,
          }}
        />
      </Flex>
    </Modal>
  );
};
